import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { ChevronLeft, ChevronRight, Loader2, Truck } from 'lucide-react';

function shipStatus(s: string): 'ok' | 'warning' | 'error' | 'info' | 'unknown' {
  const lower = (s || '').toLowerCase();
  if (lower.includes('delivered')) return 'ok';
  if (lower.includes('shipped') || lower.includes('transit')) return 'warning';
  if (lower.includes('cancel')) return 'error';
  return 'info';
}

export function ReportShipmentsTab() {
  const { sellerId } = useUIStore();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 50;

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ seller_id: String(sellerId), page: String(page), per_page: String(perPage) });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    const res = await api.get<any>(`/amazon/reports/shipments?${params}`);
    if (res.success) { setData(res.data || []); setTotal((res as any).meta?.total || 0); }
    setLoading(false);
  }, [sellerId, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Truck size={16} className="text-[var(--color-text-tertiary)]" />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]">
          <option value="all">All Statuses</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
        </select>
        <span className="text-xs text-[var(--color-text-tertiary)]">{total} shipments</span>
      </div>

      {/* Table */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">
              <th className="text-left px-3 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Shipment ID</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Amazon Order</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Date</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Status</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Delivery</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Ship To</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Carrier</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Tracking</th>
              <th className="text-right px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Net</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-16 text-[var(--color-text-tertiary)]">No shipments found</td></tr>
            ) : data.map((row) => (
              <tr key={row.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] transition-colors">
                <td className="px-3 py-3 font-mono text-xs text-[var(--color-text)]">{row.shipment_id}</td>
                <td className="px-2 py-3 font-mono text-xs text-[var(--color-text-secondary)]">{row.amazon_order_id}</td>
                <td className="px-2 py-3 text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                  {row.shipment_date ? new Date(row.shipment_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                </td>
                <td className="px-2 py-3"><StatusBadge status={shipStatus(row.shipment_status)} label={row.shipment_status || '—'} /></td>
                <td className="px-2 py-3">
                  {row.delivery_status ? (
                    <StatusBadge status={shipStatus(row.delivery_status)} label={row.delivery_status} />
                  ) : row.expected_delivery_date ? (
                    <span className="text-xs text-blue-500">{new Date(row.expected_delivery_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                  ) : '—'}
                </td>
                <td className="px-2 py-3 text-xs text-[var(--color-text)] truncate max-w-[120px]">
                  {row.ship_to_city ? `${row.ship_to_city}, ${row.ship_to_state || ''}` : '—'}
                </td>
                <td className="px-2 py-3 text-xs text-[var(--color-text-secondary)]">
                  {(row.carriers || []).filter(Boolean).join(', ') || '—'}
                </td>
                <td className="px-2 py-3 text-xs font-mono text-cyan-500">
                  {(row.tracking_numbers || []).filter(Boolean).slice(0, 1).join(', ') || '—'}
                </td>
                <td className="px-2 py-3 text-right font-mono text-xs text-[var(--color-text)]">
                  ${Number(row.net_total || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-[var(--color-text-secondary)]">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)] disabled:opacity-30"><ChevronLeft size={18} /></button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)] disabled:opacity-30"><ChevronRight size={18} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
